import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { styles } from '../styles/themes/style';

export interface IVerticalStep {
  label: string;
  sublabel?: string;
  icon?: ReactNode;
}

interface Props {
  steps: IVerticalStep[];
  /** 0-based index of the currently active step */
  currentStep: number;
  /** Override which steps are considered completed. Defaults to all indices < currentStep. */
  completedSteps?: number[];
  /** Visual size variant */
  size?: 'sm' | 'md';
  /** Accent color for active/completed dots and lines */
  accentColor?: string;
  className?: string;
}

const VerticalStepper: React.FC<Props> = ({
  steps,
  currentStep,
  completedSteps,
  size = 'md',
  accentColor = styles.colors.primary600,
  className,
}) => {
  const isDone  = (i: number) => completedSteps ? completedSteps.includes(i) : i < currentStep;
  const isActive = (i: number) => i === currentStep;

  const dotSize = size === 'sm' ? 12 : 16;

  return (
    <Wrap className={className}>
      {steps.map((step, i) => {
        const done   = isDone(i);
        const active = isActive(i);
        return (
          <StepRow key={i}>
            {/* Rail: dot + line */}
            <Rail $dotSize={dotSize}>
              <Dot
                $size={dotSize}
                $done={done}
                $active={active}
                $accent={accentColor}
              >
                {done && <CheckMark $size={dotSize}>✓</CheckMark>}
                {active && <ActiveInner $size={dotSize} $accent={accentColor} />}
              </Dot>
              {i < steps.length - 1 && (
                <Line $done={done} $accent={accentColor} />
              )}
            </Rail>

            {/* Content */}
            <Content $last={i === steps.length - 1}>
              <StepLabel $active={active} $done={done} $accent={accentColor} $size={size}>
                {step.icon && <StepIcon>{step.icon}</StepIcon>}
                {step.label}
              </StepLabel>
              {step.sublabel && (
                <StepSublabel $active={active}>{step.sublabel}</StepSublabel>
              )}
            </Content>
          </StepRow>
        );
      })}
    </Wrap>
  );
};

// ─── Styled components ────────────────────────────────────────────────────────

const Wrap = styled.div`display: flex; flex-direction: column;`;

const StepRow = styled.div`display: flex; align-items: flex-start; gap: 12px;`;

const Rail = styled.div<{ $dotSize: number }>`
  display: flex; flex-direction: column; align-items: center;
  width: ${({ $dotSize }) => $dotSize}px; flex-shrink: 0;
  padding-top: 2px;
`;

const Dot = styled.div<{ $size: number; $done: boolean; $active: boolean; $accent: string }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%; flex-shrink: 0; position: relative;
  display: flex; align-items: center; justify-content: center;
  background: ${({ $done, $active, $accent }) =>
    $done ? $accent : $active ? $accent : `${$accent}22`};
  border: 2px solid ${({ $done, $active, $accent }) =>
    ($done || $active) ? $accent : `${$accent}55`};
  transition: all 0.2s ease;
  z-index: 1;
`;

const CheckMark = styled.span<{ $size: number }>`
  font-size: ${({ $size }) => Math.round($size * 0.55)}px;
  color: white; font-weight: 800; line-height: 1;
`;

const ActiveInner = styled.div<{ $size: number; $accent: string }>`
  width: ${({ $size }) => Math.round($size * 0.4)}px;
  height: ${({ $size }) => Math.round($size * 0.4)}px;
  border-radius: 50%;
  background: ${({ $accent }) => $accent};
`;

const Line = styled.div<{ $done: boolean; $accent: string }>`
  width: 2px; flex: 1; min-height: 20px; margin: 3px 0;
  background: ${({ $done, $accent }) => $done ? `${$accent}66` : '#e2e8f0'};
  transition: background 0.2s ease;
`;

const Content = styled.div<{ $last: boolean }>`
  padding-bottom: ${({ $last }) => $last ? '0' : '20px'};
  display: flex; flex-direction: column; gap: 2px; min-width: 0;
`;

const StepLabel = styled.div<{ $active: boolean; $done: boolean; $accent: string; $size: 'sm' | 'md' }>`
  display: flex; align-items: center; gap: 6px;
  font-family: NunitoSans;
  font-size: ${({ $size }) => $size === 'sm' ? 12 : 13}px;
  font-weight: ${({ $active, $done }) => ($active || $done) ? 700 : 600};
  color: ${({ $active, $done, $accent }) =>
    $active ? $accent : $done ? styles.colors.black500 : styles.colors.black300};
  transition: color 0.2s ease;
`;

const StepIcon = styled.span`display: flex; align-items: center; flex-shrink: 0;`;

const StepSublabel = styled.div<{ $active: boolean }>`
  font-family: NunitoSans; font-size: 11px;
  color: ${({ $active }) => $active ? styles.colors.black400 : styles.colors.black300};
`;

export default VerticalStepper;
